import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import analytics from '@ad-scripts/lib/analytics';
import RoleEditor from './RoleEditor';
import RoleArchiveModal from './RoleArchiveModal';
import { isLoadingStatus } from 'src/presentation/hooks/useResource';
import OverflowMenu, {
  OverflowMenuItem,
  OverflowMenuLink,
  usePopoverMenu,
} from 'src/presentation/components/pages/shared/OverflowMenu';
import RouterLinkButton from 'src/presentation/components/RouterLinkButton';
import TablePage, { TablePageRender } from 'src/presentation/components/pages/shared/TablePage';
import rolesResource, { Role } from 'src/actions/roles/rolesResource';
import { OrderDirection } from 'src/presentation/components/pages/shared/utils/sortingUtils';
import useRoles from 'src/presentation/components/pages/people/hooks/useRoles';

const columnConfig = [{ attr: 'name', label: 'Role Name', sorting: true, customColumnStyle: () => ({ width: '50%' }) }];

const tabConfig = [
  { label: 'Active', filter: (role: Role) => !role.disabledAt, key: 'roles-tab-active' },
  { label: 'Archived', filter: (role: Role) => role.disabledAt, key: 'roles-tab-archived' },
];

const sortingConfig = { attr: 'name', direction: OrderDirection.asc };

const Roles: TablePageRender = ({ root }) => {
  const dispatch = useDispatch();
  const { roles, loadingState, resetErrors, errors } = useRoles();

  const [currentTabConfig, setCurrentTabConfig] = useState(tabConfig[0]);
  const [isArchiveModalVisible, setIsArchiveModalVisible] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  const handleArchive = async () => {
    if (!selectedRole) return;

    const action = selectedRole.disabledAt ? 'activated' : 'archived';
    const toastMessage = `"${selectedRole.name}" has been ${action}.`;
    const wasActivating = !!selectedRole.disabledAt; // true if we're activating (disabled -> enabled)

    const updatedRole = {
      ...selectedRole,
      disabledAt: wasActivating ? undefined : new Date().toISOString(),
    };
    await dispatch(
      rolesResource.effects.replace({}, updatedRole, {
        disableWindowBack: true,
        customToastMsg: toastMessage,
      })
    );

    setTimeout(() => {
      if (wasActivating && currentTabConfig.key === 'roles-tab-archived') {
        const remainingArchivedRoles = roles.filter(role => role.disabledAt && role.id !== selectedRole.id);

        if (remainingArchivedRoles.length === 0) {
          setCurrentTabConfig(tabConfig[0]); // Switch to "Active" tab
        }
      }
    });

    setIsArchiveModalVisible(false);
    setSelectedRole(null);
  };

  const openArchiveModal = role => {
    setSelectedRole(role);
    setIsArchiveModalVisible(true);
  };

  const closeArchiveModal = () => {
    setIsArchiveModalVisible(false);
    setSelectedRole(null);
  };

  const renderRowActions = ({ item }) => {
    return <RowActions item={item} root={root} openArchiveModal={openArchiveModal} />;
  };

  const actionsRender = (
    <RouterLinkButton data-aid="roles-add-button" data-testid="roles-add-button" to={`${root}/new`}>
      Add Role
    </RouterLinkButton>
  );

  // TODO: fix help link - https://app.shortcut.com/gladly/story/212326/ad-replace-help-link-with-correct-one-when-documentation-will-be-ready
  const description = (
    <span>
      Configure and manage additional Roles that can be assigned to Users. To learn more about Roles, visit our{' '}
      <a href="https://help.gladly.com/docs/managing-users-and-roles" target="_blank" rel="noopener noreferrer">
        Help Docs
      </a>
      .
    </span>
  );

  return (
    <>
      <TablePage
        actions={actionsRender}
        columnConfig={columnConfig}
        data-aid="roles-page"
        data-testid="roles-page"
        description={description}
        editor={RoleEditor}
        emptyStateFullHeightLayout
        errors={errors}
        filterFunc={filterFunc}
        initialSorting={sortingConfig}
        isLoading={isLoadingStatus(loadingState)}
        items={roles}
        name="Roles"
        onTabChange={setCurrentTabConfig}
        renderRowActions={renderRowActions}
        resetErrors={resetErrors}
        preselectedTab={currentTabConfig.key}
        root={root}
        tabConfig={tabConfig}
      />
      <RoleArchiveModal
        isVisible={isArchiveModalVisible}
        selectedRole={selectedRole}
        onCancel={closeArchiveModal}
        onSubmit={handleArchive}
      />
    </>
  );
};

const filterFunc = (items, searchString) => {
  const searchPaths = ['name'];
  if (searchString) {
    const search = searchString.toLowerCase().trim();
    return items.filter(item => searchPaths.some(columnKey => (item[columnKey] || '').toLowerCase().includes(search)));
  }
  return items;
};

const RowActions = ({ root, item, openArchiveModal }) => {
  const { onClose, ...popoverMenuProps } = usePopoverMenu();

  const handleEdit = () => {
    analytics.track('Admin - Roles - Edit Role', {
      roleId: item.id,
    });
    onClose();
  };

  const handleArchiveToggle = () => {
    analytics.track(`Admin - Roles - ${item.disabledAt ? 'Active' : 'Archive'} Role`, {
      roleId: item.id,
    });
    onClose();
    openArchiveModal(item);
  };

  if (item.builtin) {
    return (
      <OverflowMenu id={item.id} onClose={onClose} {...popoverMenuProps}>
        <OverflowMenuItem id={`viewRole-${item.id}`}>
          <OverflowMenuLink to={`${root}/${item.id}`} onClick={handleEdit}>
            View
          </OverflowMenuLink>
        </OverflowMenuItem>
      </OverflowMenu>
    );
  }

  return (
    <OverflowMenu id={item.id} onClose={onClose} {...popoverMenuProps}>
      <OverflowMenuItem id={`editRole-${item.id}`}>
        <OverflowMenuLink to={`${root}/${item.id}`} onClick={handleEdit}>
          Edit
        </OverflowMenuLink>
      </OverflowMenuItem>
      <OverflowMenuItem id={`archiveRole-${item.disabledAt ? 'archive' : 'active'}`}>
        <OverflowMenuLink onClick={handleArchiveToggle} to="#">
          {item.disabledAt ? 'Active' : 'Archive'}
        </OverflowMenuLink>
      </OverflowMenuItem>
    </OverflowMenu>
  );
};

export default Roles;
